import { type Product } from '@scayle/storefront-nuxt'

import {
  MAIN_CATEGORY_ROOT_IDS,
  LIFESTYLE_CATEGORY_ROOT_IDS,
  SALE_CATEGORY_ROOT_IDS,
} from '#layers/base/constants/scayle'
import {
  getProductCategoryBreadcrumbs,
  selectDefaultBreadcrumbRootPath,
  selectNavigationContextPath,
  type ProductCategoryNode,
} from '#layers/base/utils/breadcrumb'
import usePdpBreadcrumbContext from '#layers/base/composables/usePdpBreadcrumbContext'

/**
 * Legacy selection: pick the breadcrumb path by matching a path's actual root
 * id against the hardcoded brand root-id lists, preferring main categories over
 * lifestyle/sale for SEO. Returns the deepest matching path.
 *
 * Kept as a fallback for products whose categories are not yet tagged with the
 * `PDPIsDefaultBreadcrumbRoot` attribute. Remove once the attribute is verified
 * live in production for both brands (ECOM-2381).
 */
const selectLegacyMainPath = (
  categories: ProductCategoryNode[][],
): ProductCategoryNode[] | null => {
  const matchByRoot = (ids: number[]) =>
    categories.filter((c) => {
      const rootId = c[0]?.categoryId
      return rootId !== undefined && ids.includes(rootId)
    })

  let candidates = matchByRoot(MAIN_CATEGORY_ROOT_IDS)

  if (!candidates.length) {
    /* HINT: Main category should always take priority in breadcrumbs for SEO
     * reasons. Some products are listed both in main and sale or lifestyle, so
     * we check the other roots only if no main was found. */
    candidates = matchByRoot([
      ...LIFESTYLE_CATEGORY_ROOT_IDS,
      ...SALE_CATEGORY_ROOT_IDS,
    ])
  }

  if (!candidates.length) {
    return null
  }

  return candidates.reduce((deepest, current) =>
    current.length > deepest.length ? current : deepest,
  )
}

export const useProductBreadcrumbs = (product: Product | null) => {
  const categories = (product?.categories ?? []) as ProductCategoryNode[][]

  // Data-driven selection (ECOM-2381): prefer the path rooted at a category
  // tagged as a default breadcrumb root in Scayle. This stays the source of the
  // "master" path for size-guide / sizekick consumers.
  const selectedCategoryPath =
    selectDefaultBreadcrumbRootPath(categories) ??
    selectLegacyMainPath(categories) ??
    []

  // In-app navigation override (ECOM-2383): when the customer reached this PDP by
  // clicking a product on a PLP, prefer the product category path that starts with
  // that PLP's category path. Falls back to the ECOM-2381 path when there is no
  // navigation context (deeplink, reload, slider, search, ...) or no path fits.
  // The override only changes the rendered breadcrumb — not `selectedCategoryPath`.
  const { getNavigationContextFor } = usePdpBreadcrumbContext()
  const contextIds = getNavigationContextFor(product?.id)
  const navigationCategoryPath = contextIds
    ? selectNavigationContextPath(categories, contextIds)
    : null

  // ECOM-2384: resolve each crumb to its cmsPagePath when set (matching nav),
  // instead of Scayle's getBreadcrumbs which always uses categoryUrl.
  const breadcrumbs = getProductCategoryBreadcrumbs(
    navigationCategoryPath ?? selectedCategoryPath,
  )

  return {
    breadcrumbs,
    selectedCategoryPath,
    // Back-compat: consumers (`getDeepestCategory`, size guide) expect an array
    // of candidate paths.
    mainCategories: selectedCategoryPath.length ? [selectedCategoryPath] : [],
  }
}

export default useProductBreadcrumbs
